import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildInitialValues,
  getVisibleGroups,
  isEmptyValue,
  parseFileEntries,
  resolveWidgetKind,
} from '../helpers';
import { validateForm, validateField } from '../validators';
import type { FormField, FormGroup, FormStateValues, FormSubmitValues } from '../types';

/**
 * Versioned-document fields submit `{document_submission_id, document_version_id, ...}`
 * (single object) for `allow_multiple: false` and an array of those objects
 * otherwise — per the backend contract documented alongside
 * `Document-Upload.md`. The picker internally always holds an array; this
 * function unwraps single-file fields at submit time.
 */
const serializePickerValue = (field: FormField, value: unknown): unknown => {
  const entries = parseFileEntries(value).map((e) => {
    const out: Record<string, unknown> = {
      document_submission_id: e.document_submission_id,
      document_version_id: e.document_version_id,
    };
    if (e.filename) out.filename = e.filename;
    if (e.mime_type) out.mime_type = e.mime_type;
    return out;
  });
  if (entries.length === 0) return undefined;
  const allowMultiple = field.constraints?.allow_multiple !== false;
  return allowMultiple ? entries : entries[0];
};

const isVersionedPicker = (field: FormField): boolean => {
  const kind = resolveWidgetKind(field);
  return kind === 'versioned_image_picker' || kind === 'versioned_pdf_picker';
};

interface UseDynamicFormArgs {
  groups: FormGroup[];
  initialValues?: FormStateValues;
}

interface UseDynamicFormResult {
  values: FormStateValues;
  errors: Record<string, string>;
  /** Ever-attempted submit — used to decide whether to show inline errors. */
  submitted: boolean;
  setValue: (title: string, value: unknown) => void;
  setValues: (next: FormStateValues) => void;
  resetTouched: () => void;
  /**
   * Validates and returns the prepared submission payload. When the form is
   * invalid, returns `null` and populates `errors`.
   *
   * `overlay` is merged on top of the current `values` before validation —
   * used by the submit pass to apply just-uploaded picker values without
   * waiting for React state to flush.
   */
  validateAndPrepareSubmit: (overlay?: FormStateValues) => FormSubmitValues | null;
}

/**
 * Stateful hook for the metadata-driven form. Owns:
 *   - controlled values (per `field.title`)
 *   - inline error map (cleared as users edit a field)
 *   - submit-time validation that walks every visible field
 */
export const useDynamicForm = ({ groups, initialValues }: UseDynamicFormArgs): UseDynamicFormResult => {
  const visibleGroups = useMemo(() => getVisibleGroups(groups), [groups]);

  const [values, setValuesState] = useState<FormStateValues>(() => ({
    ...buildInitialValues(visibleGroups),
    ...(initialValues || {}),
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // If the schema arrives async, refresh defaults once we have it.
  useEffect(() => {
    setValuesState((prev) => {
      const defaults = buildInitialValues(visibleGroups);
      return { ...defaults, ...prev };
    });
  }, [visibleGroups]);

  const setValue = useCallback(
    (title: string, value: unknown) => {
      setValuesState((prev) => ({ ...prev, [title]: value }));
      // Re-validate this field once we already attempted submit so the user
      // sees the error clear as soon as they fix it.
      setErrors((prev) => {
        if (!(title in prev)) return prev;
        const field = visibleGroups
          .flatMap((g) => g.items)
          .find((f) => f.title === title);
        if (!field) return prev;
        const err = validateField(field, value);
        const next = { ...prev };
        if (err) next[title] = err;
        else delete next[title];
        return next;
      });
    },
    [visibleGroups],
  );

  const setValues = useCallback((next: FormStateValues) => {
    setValuesState((prev) => ({ ...prev, ...next }));
  }, []);

  const resetTouched = useCallback(() => {
    setErrors({});
    setSubmitted(false);
  }, []);

  const validateAndPrepareSubmit = useCallback(
    (overlay?: FormStateValues): FormSubmitValues | null => {
      setSubmitted(true);
      const effective = overlay ? { ...values, ...overlay } : values;
      const map = validateForm(visibleGroups, effective);
      setErrors(map);
      if (Object.keys(map).length > 0) return null;

      // Build the submission payload — drop empty values so the backend treats
      // them as omissions rather than empty strings.
      const payload: FormSubmitValues = {};
      for (const group of visibleGroups) {
        for (const field of group.items) {
          const v = effective[field.title];
          if (isVersionedPicker(field)) {
            const serialized = serializePickerValue(field, v);
            if (serialized === undefined) continue;
            payload[field.title] = serialized;
            continue;
          }
          if (isEmptyValue(v)) continue;
          payload[field.title] = v;
        }
      }
      return payload;
    },
    [values, visibleGroups],
  );

  return {
    values,
    errors,
    submitted,
    setValue,
    setValues,
    resetTouched,
    validateAndPrepareSubmit,
  };
};
